import { useEffect, useId, useMemo, useState } from "react";
import { ChevronDown, List, MapPin } from "lucide-react";
import { parseTocItems, type TocItem } from "@/lib/tableOfContents";

export { headingToId, parseTocItems, type TocItem } from "@/lib/tableOfContents";

interface Props {
  content: string;
  /** Sticky right-rail navigation on qualifying desktop guides, or an inline collapsible navigation below the hero. */
  variant: "sidebar" | "inline";
  /** Keeps this navigation variant visible at xl widths when the parent requests it. */
  showOnDesktop?: boolean;
  locale?: "en" | "ar";
}

const labels = {
  en: { aria: "Table of contents", heading: "On this page", mobileHeading: "In this article", section: "Section", of: "of", sections: "sections" },
  ar: { aria: "فهرس المحتويات", heading: "في هذه الصفحة", mobileHeading: "في هذه المقالة", section: "القسم", of: "من", sections: "أقسام" },
} as const;

function sectionPosition(items: TocItem[], id: string): number {
  const itemIndex = items.findIndex((item) => item.id === id);
  if (itemIndex < 0) return 0;
  return items.slice(0, itemIndex + 1).filter((item) => item.level === 2).length;
}

/**
 * Accessible, progressive table-of-contents navigation for Markdown and legacy
 * HTML-authored articles. Native fragment links preserve no-JavaScript behavior,
 * while the observer adds a reading-position cue after hydration.
 */
export default function TableOfContents({ content, variant, showOnDesktop = false, locale = "en" }: Props) {
  const items = useMemo(() => parseTocItems(content), [content]);
  const copy = labels[locale];
  const listId = useId().replace(/:/g, "");
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setActiveId(items[0]?.id ?? "");
  }, [items]);

  useEffect(() => {
    if (!items.length) return;

    const headingElements = items
      .map((item) => document.getElementById(item.id))
      .filter((element): element is HTMLElement => element instanceof HTMLElement);
    if (!headingElements.length) return;

    const updateActiveHeading = () => {
      const threshold = 128;
      const passed = headingElements.filter((heading) => heading.getBoundingClientRect().top <= threshold);
      setActiveId((passed.at(-1) ?? headingElements[0]).id);
    };

    const observer = new IntersectionObserver(
      () => updateActiveHeading(),
      { rootMargin: "-112px 0px -58% 0px", threshold: [0, 0.1, 1] },
    );

    headingElements.forEach((heading) => observer.observe(heading));
    window.addEventListener("scroll", updateActiveHeading, { passive: true });
    window.addEventListener("resize", updateActiveHeading, { passive: true });
    updateActiveHeading();

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updateActiveHeading);
      window.removeEventListener("resize", updateActiveHeading);
    };
  }, [items]);

  if (!items.length) return null;

  const primarySectionCount = items.filter((item) => item.level === 2).length;

  const list = (className: string) => (
    <ol id={listId} className={className}>
      {items.map((item) => {
        const isActive = activeId === item.id;
        const parentPosition = sectionPosition(items, item.id);
        return (
          <li key={item.id} className={item.level === 3 ? "ps-4" : item.level === 4 ? "ps-7" : ""}>
            <a
              href={`#${encodeURIComponent(item.id)}`}
              onClick={() => {
                setActiveId(item.id);
                setOpen(false);
              }}
              aria-current={isActive ? "location" : undefined}
              className={[
                "group block w-full rounded-md text-start transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                item.level === 2 ? "py-1.5" : item.level === 3 ? "py-1 text-[0.8rem]" : "py-1 text-[0.75rem]",
                isActive
                  ? "bg-amber-500/10 font-semibold text-amber-700 dark:text-amber-300"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              ].join(" ")}
            >
              <span className="flex items-start gap-2 px-2">
                {item.level === 2 ? (
                  <span className={[
                    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                    isActive ? "bg-amber-500 text-slate-950" : "bg-muted-foreground/10 text-muted-foreground",
                  ].join(" ")}>
                    {parentPosition}
                  </span>
                ) : item.level === 3 ? (
                  <span className="mt-1.5 h-px w-3 shrink-0 bg-current opacity-35" aria-hidden="true" />
                ) : (
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-35" aria-hidden="true" />
                )}
                <span className="min-w-0 leading-snug">{item.text}</span>
              </span>
            </a>
          </li>
        );
      })}
    </ol>
  );

  if (variant === "sidebar") {
    return (
      <nav aria-label={copy.aria} className={["sticky top-28 hidden w-60 shrink-0 self-start", showOnDesktop ? "xl:block" : ""].join(" ")}>
        <div className="rounded-xl border border-border bg-card/65 p-3 shadow-sm backdrop-blur-sm">
          <div className="mb-3 flex items-center gap-2 px-1">
            <MapPin className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{copy.heading}</p>
          </div>
          <div className="max-h-[calc(100vh-10rem)] overflow-y-auto pe-1 [scrollbar-width:thin]">
            {list("space-y-0.5")}
          </div>
          <div className="mt-3 border-t border-border px-1 pt-2 text-[11px] text-muted-foreground">
            {copy.section} {Math.max(1, sectionPosition(items, activeId))} {copy.of} {primarySectionCount}
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav aria-label={copy.aria} className={["mb-8 rounded-xl border border-border bg-muted/35", showOnDesktop ? "" : "xl:hidden"].join(" ")}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-inset"
        aria-expanded={open}
        aria-controls={listId}
      >
        <span className="flex min-w-0 items-center gap-2">
          <List className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          <span className="font-semibold text-foreground">{copy.mobileHeading}</span>
          <span className="hidden text-xs text-muted-foreground sm:inline">{primarySectionCount} {copy.sections}</span>
        </span>
        <ChevronDown className={["h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200", open ? "rotate-180" : ""].join(" ")} aria-hidden="true" />
      </button>

      {open && <div className="border-t border-border px-3 py-3">{list("space-y-0.5")}</div>}
    </nav>
  );
}
